// Search Grid Component (simplified - no sorting or colorways)
class SearchGrid extends HTMLElement {
    // Constants
    static get CONSTANTS() {
        return {
            DELAYS: {
                LAYOUT_UPDATE: 100,
                TRANSITION: 150
            },
            SELECTORS: {
                PRODUCT_CARDS: 'search-grid product-card',
                HEADER_COUNT: '.search-main__header-count',
                FOOTER: 'footer-component',
                SEARCH_LOAD_MORE: 'search-load-more'
            }
        };
    }

    constructor() {
        super();
        this.isInitialLoad = true;
        this.setupEventListeners();
        this.initializeLayout();
    }

    async initializeLayout() {
        await this.updateLayout();
        
        setTimeout(async () => {
            this.applyDiscountBackgroundColors();
            await this.updateHeaderCount(true);
            this.isInitialLoad = false;
        }, 100);
    }

    setupEventListeners() {
        document.addEventListener('search:updated', (event) => {
            this.handleSearchUpdated(event);
        });

        document.addEventListener('search-load-more:products-loaded', (event) => {
            this.handleSearchLoadMoreProductsLoaded(event);
        });

        document.addEventListener('search-load-more:complete', (event) => {
            this.handleSearchLoadMoreComplete(event);
        });
    }

    async handleSearchUpdated(event) {
        await this.updateHeaderCount(false);
    }

    handleSearchLoadMoreProductsLoaded(event) {
        this.updateProductCount();
    }

    handleSearchLoadMoreComplete(event) {
        const { totalPages, currentPage } = event.detail;
        console.log(`Search Load More completed. Loaded ${currentPage} of ${totalPages} pages.`);
    }

    async updateLayout() {
        this.dispatchLayoutUpdatedEvent();
        this.finalizeLayoutUpdate();
    }

    dispatchLayoutUpdatedEvent() {
        this.dispatchEvent(new CustomEvent('search-grid-layout-updated', {
            detail: { layout: '4' },
            bubbles: true
        }));
    }

    async updateProductCount() {
        await this.updateHeaderCount(false);
    }

    finalizeLayoutUpdate() {
        setTimeout(() => {
            this.classList.remove("search-grid--transitioning");
        }, SearchGrid.CONSTANTS.DELAYS.TRANSITION);

        this.refreshFooter();
    }

    getTotalProductCount() {
        const totalProductsElement = document.querySelector('.search-main__total-products');
        return totalProductsElement ? parseInt(totalProductsElement.textContent) || 0 : 0;
    }

    async updateHeaderCount(useTrueCount = false) {
        const headerCountElement = document.querySelector(SearchGrid.CONSTANTS.SELECTORS.HEADER_COUNT);
        if (!headerCountElement) return;

        if (useTrueCount) {
            const totalProducts = this.getTotalProductCount();
            const span = headerCountElement.querySelector('span');
            if (span) {
                span.textContent = totalProducts;
            }
        }

        headerCountElement.style.display = 'block';
    }

    updateLoadingState(state, count) {
        // No loading state needed since we have infinite scroll
    }

    refreshFooter() {
        // Footer refresh no longer needed - only newsletter bounce functionality remains
    }

    applyDiscountBackgroundColors() {
        if (window.applyDiscountBackgroundColors) {
            window.applyDiscountBackgroundColors(this);
        }
    }
}

// Search Load More Component (simplified - no colorways or complex duplicate handling)
class SearchLoadMore extends HTMLElement {
    static get CONSTANTS() {
        return {
            DELAYS: {
                LOAD_DELAY: 300
            },
            SELECTORS: {
                SEARCH_GRID: 'search-grid',
                PRODUCT_CARDS: 'search-grid product-card',
                HAS_MORE_PAGES: '.search-main__has-more-pages',
                CURRENT_PAGE: '.search-main__current-page',
                TOTAL_PAGES: '.search-main__total-pages'
            },
            STATES: {
                LOADING: 'loading',
                ERROR: 'error',
                COMPLETE: 'complete'
            },
            SCROLL_THRESHOLD: 0.05
        };
    }

    constructor() {
        super();
        
        this.currentPage = parseInt(this.getAttribute('data-current-page')) || 1;
        this.totalPages = parseInt(this.getAttribute('data-total-pages')) || 1;
        this.hasMorePages = this.getAttribute('data-has-more') === 'true';
        
        this.isLoading = false;
        this.scrollHandler = null;
        this.searchGrid = null;
        // Footer refresh no longer needed - only newsletter bounce functionality remains
        
        this.initialProductsPerPage = parseInt(document.querySelector('.search-main__current-limit')?.textContent) || 48;
        this.fetchProductsPerPage = 1000;
        this.totalProducts = parseInt(document.querySelector('.search-main__total-products')?.textContent) || 0;
        
        this.initialLoadedProducts = this.initialProductsPerPage;
        this.loadedProductsCount = this.initialProductsPerPage;
    }

    connectedCallback() {
        this.setupScrollHandler();
        this.updateState();
    }

    disconnectedCallback() {
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler, { passive: true });
        }
    }

    setupCollectionGrid() {
        this.searchGrid = document.querySelector(SearchLoadMore.CONSTANTS.SELECTORS.SEARCH_GRID);
        
        if (!this.searchGrid) {
            setTimeout(() => {
                this.setupCollectionGrid();
            }, 500);
            return;
        }
    }

    setupScrollHandler() {
        this.setupCollectionGrid();
        this.scrollHandler = this.handleScroll.bind(this);
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    handleScroll() {
        if (!this.shouldLoadMore() || !this.searchGrid) return;

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const gridRect = this.searchGrid.getBoundingClientRect();
        
        const gridTop = gridRect.top;
        const gridBottom = gridRect.bottom;
        const gridHeight = this.searchGrid.offsetHeight;
        
        let visiblePercentage = 0;
        
        if (gridBottom > 0 && gridTop < windowHeight) {
            const visibleTop = Math.max(0, gridTop);
            const visibleBottom = Math.min(windowHeight, gridBottom);
            const visibleHeight = visibleBottom - visibleTop;
            visiblePercentage = visibleHeight / gridHeight;
        }
        
        if (visiblePercentage >= SearchLoadMore.CONSTANTS.SCROLL_THRESHOLD) {
            this.loadMoreProducts();
        }
    }

    shouldLoadMore() {
        return !this.isLoading && 
               this.loadedProductsCount < this.totalProducts && 
               this.hasMorePages;
    }

    async loadMoreProducts() {
        if (this.isLoading) return;

        this.isLoading = true;
        this.updateState();

        try {
            const nextPage = this.currentPage + 1;
            const url = this.buildNextPageUrl(nextPage);
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            const newProducts = this.extractProductsFromResponse(doc);
            
            if (newProducts.length > 0) {
                this.appendNewProducts(newProducts);
                this.currentPage = nextPage;
                this.loadedProductsCount += newProducts.length;
                this.updatePaginationState();

                // Footer refresh no longer needed - only newsletter bounce functionality remains
            }

            if (this.loadedProductsCount >= this.totalProducts) {
                this.hasMorePages = false;
                this.markAsComplete();
            } else {
                this.updatePaginationState();
            }

        } catch (error) {
            this.handleError();
        } finally {
            this.isLoading = false;
            this.updateState();
        }
    }

    buildNextPageUrl(nextPage) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('page', nextPage.toString());
        currentUrl.searchParams.set('view', 'grid-data');
        
        return currentUrl.toString();
    }

    extractProductsFromResponse(doc) {
        const searchGrid = doc.querySelector(SearchLoadMore.CONSTANTS.SELECTORS.SEARCH_GRID);
        if (!searchGrid) {
            return [];
        }

        this.updatePaginationFromResponse(doc);
        const productCards = Array.from(searchGrid.querySelectorAll('product-card'));
        
        let newProductCards;
        if (productCards.length === this.totalProducts) {
            newProductCards = productCards.slice(this.loadedProductsCount);
        } else {
            newProductCards = productCards;
        }
        
        return newProductCards.map(card => {
            const clonedCard = card.cloneNode(true);
            return clonedCard;
        });
    }

    updatePaginationFromResponse(doc) {
        const responseLoadMore = doc.querySelector('search-load-more');
        if (responseLoadMore) {
            const newTotalPages = parseInt(responseLoadMore.getAttribute('data-total-pages'));
            const newHasMore = responseLoadMore.getAttribute('data-has-more') === 'true';
            
            if (newTotalPages && newTotalPages !== this.totalPages) {
                this.totalPages = newTotalPages;
            }
            
            if (newHasMore !== this.hasMorePages) {
                this.hasMorePages = newHasMore;
            }
        }
    }

    appendNewProducts(newProducts) {
        const searchGrid = document.querySelector(SearchLoadMore.CONSTANTS.SELECTORS.SEARCH_GRID);
        
        if (!searchGrid) {
            return;
        }

        // Hide skeleton container if it's visible
        const skeletonContainer = document.querySelector('.search-skeleton-container');
        if (skeletonContainer && skeletonContainer.style.display !== 'none') {
            skeletonContainer.style.display = 'none';
        }

        // Simple duplicate removal based on product handles
        const existingHandles = new Set();
        const existingProductCards = searchGrid.querySelectorAll('product-card');
        existingProductCards.forEach(card => {
            const handle = card.getAttribute('product-handle');
            if (handle) {
                existingHandles.add(handle);
            }
        });

        const uniqueNewProducts = newProducts.filter(productCard => {
            const handle = productCard.getAttribute('product-handle');
            return handle && !existingHandles.has(handle);
        });

        uniqueNewProducts.forEach(productCard => {
            productCard.classList.add('product-card--visible');
            searchGrid.appendChild(productCard);
        });

        this.applyDiscountBackgroundColors();

        this.dispatchEvent(new CustomEvent('search-load-more:products-loaded', {
            detail: {
                newProducts: uniqueNewProducts.length,
                totalRequested: newProducts.length,
                duplicatesSkipped: newProducts.length - uniqueNewProducts.length,
                currentPage: this.currentPage
            },
            bubbles: true
        }));
        
        this.checkFooterAnimationState();
    }

    updatePaginationState() {
        const currentPageElement = document.querySelector(SearchLoadMore.CONSTANTS.SELECTORS.CURRENT_PAGE);
        const totalPagesElement = document.querySelector(SearchLoadMore.CONSTANTS.SELECTORS.TOTAL_PAGES);
        const hasMoreElement = document.querySelector(SearchLoadMore.CONSTANTS.SELECTORS.HAS_MORE_PAGES);

        if (currentPageElement) {
            currentPageElement.textContent = this.currentPage;
        }

        if (hasMoreElement) {
            hasMoreElement.setAttribute('data-has-more', this.hasMorePages.toString());
        }

        this.setAttribute('data-current-page', this.currentPage.toString());
        this.setAttribute('data-total-pages', this.totalPages.toString());
        this.setAttribute('data-has-more', this.hasMorePages.toString());
        
        this.checkFooterAnimationState();
    }

    updateState() {
        this.classList.toggle('search-load-more--loading', this.isLoading);
        setTimeout(() => {
            this.classList.toggle('search-load-more--complete', !this.hasMorePages);
        }, 500);
    }

    markAsComplete() {
        this.hasMorePages = false;
        this.isLoading = false;
        this.updateState();
        this.updatePaginationState();
        
        this.dispatchEvent(new CustomEvent('search-load-more:complete', {
            detail: {
                totalProducts: this.totalProducts,
                loadedProducts: this.loadedProductsCount,
                currentPage: this.currentPage
            },
            bubbles: true
        }));
        
        this.checkFooterAnimationState();
    }

    handleError() {
        this.classList.add('search-load-more--error');
        
        this.dispatchEvent(new CustomEvent('search-load-more:error', {
            detail: {
                currentPage: this.currentPage,
                error: 'Failed to load more products'
            },
            bubbles: true
        }));
    }

    checkFooterAnimationState() {
        setTimeout(() => {
            const footerComponent = document.querySelector('footer-component');
            if (footerComponent && typeof footerComponent.handleAnimationStateChange === 'function') {
                footerComponent.handleAnimationStateChange();
            }
        }, 100);
    }

    applyDiscountBackgroundColors() {
        if (this.searchGrid && window.applyDiscountBackgroundColors) {
            window.applyDiscountBackgroundColors(this.searchGrid);
        }
    }

    resetState() {
        this.currentPage = parseInt(this.getAttribute('data-current-page')) || 1;
        this.totalPages = parseInt(this.getAttribute('data-total-pages')) || 1;
        this.hasMorePages = this.getAttribute('data-has-more') === 'true';
        this.isLoading = false;
        
        // Reset loaded products count based on current pagination
        const totalProducts = parseInt(document.querySelector('.search-main__total-products')?.textContent) || 0;
        const paginationLimit = parseInt(document.querySelector('.search-main__current-limit')?.textContent) || 48;
        
        this.totalProducts = totalProducts;
        this.loadedProductsCount = Math.min(paginationLimit, totalProducts);
        this.initialLoadedProducts = Math.min(paginationLimit, totalProducts);
        
        this.updateState();
        
    }
}

// Main Search Component (handles tab clicks and coordinates everything)
class SearchMain extends HTMLElement {
    static get CONSTANTS() {
        return {
            DEBOUNCE_DELAY: 150,
            FETCH_DELAY: 150,
            TRUSTED_DOMAINS: ["vessi.com", "www.vessi.com"],
            SELECTORS: {
                SEARCH_TABS: '.search-header__tabs a',
                SEARCH_GRID: 'search-grid',
                SEARCH_MAIN: '.search-main'
            }
        };
    }

    constructor() {
        super();
        this.initializeProperties();
        this.bindMethods();
    }

    initializeProperties() {
        this.currentUrl = this.sanitizeUrl(window.location.href);
        this.url = new URL(this.currentUrl);
        this.searchParams = this.url.searchParams;
        this.abortController = new AbortController();
    }

    bindMethods() {
        this.handleTabClick = this.debounce(this.handleTabClick.bind(this), SearchMain.CONSTANTS.DEBOUNCE_DELAY);
    }

    connectedCallback() {
        // Use setTimeout to ensure DOM is fully ready
        setTimeout(() => {
            this.initializeElements();
            this.attachEventListeners();
            this.checkForActiveTabOnLoad();
        }, 100);
    }

    disconnectedCallback() {
        this.abortController.abort();
    }

    initializeElements() {
        const { SELECTORS } = SearchMain.CONSTANTS;
        
        this.searchTabs = document.querySelectorAll(SELECTORS.SEARCH_TABS);
        this.searchGrid = document.querySelector(SELECTORS.SEARCH_GRID);
        this.searchMain = document.querySelector(SELECTORS.SEARCH_MAIN);
        this.searchLoadMore = document.querySelector('search-load-more');
        
    }

    attachEventListeners() {
        if (this.searchTabs?.length > 0) {
            this.searchTabs.forEach(tab => {
                // Remove any existing listeners first
                tab.removeEventListener('click', this.handleTabClick);
                // Add the listener with capture: true to ensure it runs first
                tab.addEventListener('click', this.handleTabClick, { capture: true });
                
                // Also set onclick as a fallback to prevent navigation
                tab.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    this.handleTabClick(e);
                    return false;
                };
                
            });
        }
    }

    checkForActiveTabOnLoad() {
        // Check if there's an active tab on page load
        const activeTab = document.querySelector('.search-header__tabs a.active');
        
        if (activeTab && activeTab.href) {
            // Get the current search type from search-grid data-type attribute
            const searchGrid = document.querySelector('search-grid');
            const currentType = searchGrid?.getAttribute('data-type') || '';
            
            // Build the fetch URL with the current type
            const fetchUrl = this.buildFetchUrl(activeTab.href, currentType);
            
            // Fetch the content for the active tab
            this.fetchSearchResults(fetchUrl);
        } else {

            this.hideSkeletonCards();


            document.querySelector("search-grid").style.display = 'grid';

            // Footer refresh no longer needed - only newsletter bounce functionality remains

        }
    }

    handleTabClick(e) {
        // Prevent default behavior and stop propagation
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const clickedTab = e.target;
        const url = clickedTab.href;
        
        if (!url) {
            return false;
        }

        // Update active tab immediately for better UX
        this.updateActiveTab(clickedTab);
        
        // Show skeleton cards while loading
        this.showSkeletonCards();
        
        // Get the current search type from search-grid data-type attribute
        const searchGrid = document.querySelector('search-grid');
        const currentType = searchGrid?.getAttribute('data-type') || '';
        
        // Build the fetch URL with the current type
        const fetchUrl = this.buildFetchUrl(url, currentType);
        
        // Fetch and replace search grid content
        this.fetchSearchResults(fetchUrl);
        
        // Return false as additional prevention
        return false;
    }

    buildFetchUrl(originalUrl, currentType) {
        const url = new URL(originalUrl);
        
        // Ensure the search query is properly encoded
        const searchQuery = url.searchParams.get('q');
        if (searchQuery) {
            // Convert HTML entities to actual characters first
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = searchQuery;
            const decodedQuery = tempDiv.textContent || tempDiv.innerText || '';
            
            // Now properly encode the decoded query
            url.searchParams.set('q', decodedQuery);
        }
        
        // Add the current type parameter if it exists
        if (currentType) {
            url.searchParams.set('type', currentType);
        }
        
        return url.toString();
    }

    updateActiveTab(activeTab) {
        this.searchTabs.forEach(tab => {
            tab.classList.remove('active');
        });
        
        activeTab.classList.add('active');
    }

    fetchSearchResults(url) {
        const safeUrl = this.sanitizeUrl(url);
        if (!safeUrl) {
            console.warn('SearchMain: Unsafe URL detected');
            return;
        }

        const fetchUrl = new URL(safeUrl);
        fetchUrl.searchParams.set('view', 'grid-data');

        this.dispatchEvent(new CustomEvent('search:fetching', { bubbles: true }));

        fetch(fetchUrl.toString(), {
            redirect: "manual",
            signal: this.abortController.signal
        })
        .then(response => this.validateResponse(response))
        .then(response => response.text())
        .then(html => this.processFetchedHtml(html, safeUrl))
        .catch(error => this.handleFetchError(error));
    }

    validateResponse(response) {
        const finalUrl = response.url;
        if (!this.isValidUrl(finalUrl)) {
            throw new Error("Unsafe redirect detected!");
        }
        return response;
    }

    isValidUrl(userUrl) {
        const { TRUSTED_DOMAINS } = SearchMain.CONSTANTS;
        try {
            const url = new URL(userUrl);
            return TRUSTED_DOMAINS.some(domain => 
                url.hostname === domain || url.hostname.endsWith('.' + domain)
            );
        } catch {
            return false;
        }
    }

    processFetchedHtml(html, url) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSearchGrid = doc.querySelector('search-grid');
        const newSearchLoadMore = doc.querySelector('search-load-more');

        // Replace search grid content asynchronously
        if (newSearchGrid && this.searchGrid) {
            // Hide skeleton cards first
            this.hideSkeletonCards();
            
            // Update grid attributes
            const newGridAttributes = {
                'data-current-page': newSearchGrid.getAttribute('data-current-page'),
                'data-total-pages': newSearchGrid.getAttribute('data-total-pages'),
                'layout': newSearchGrid.getAttribute('layout')
            };
            
            Object.entries(newGridAttributes).forEach(([attr, value]) => {
                if (value !== null) {
                    this.searchGrid.setAttribute(attr, value);
                }
            });
            
            // Update all pagination elements
            this.updatePaginationElements(doc);
            
            // Replace the entire grid content
            this.searchGrid.innerHTML = newSearchGrid.innerHTML;
            
            // Apply discount background colors to new content
            this.applyDiscountBackgroundColors();
            
            // Make product cards visible
            this.makeProductCardsVisible();

            document.querySelector("search-grid").style.display = 'grid';

            // Footer refresh no longer needed - only newsletter bounce functionality remains

        }

        // Update search load more component
        if (newSearchLoadMore && this.searchLoadMore) {
            const newCurrentPage = parseInt(newSearchLoadMore.getAttribute('data-current-page')) || 1;
            const newTotalPages = parseInt(newSearchLoadMore.getAttribute('data-total-pages')) || 1;
            const newHasMore = newSearchLoadMore.getAttribute('data-has-more') === 'true';
            
            // Update attributes
            this.searchLoadMore.setAttribute('data-current-page', newCurrentPage.toString());
            this.searchLoadMore.setAttribute('data-total-pages', newTotalPages.toString());
            this.searchLoadMore.setAttribute('data-has-more', newHasMore.toString());
            
            // Reset internal state with new values
            if (typeof this.searchLoadMore.resetState === 'function') {
                this.searchLoadMore.resetState();
            }
            
            // Force update the component's internal properties
            this.searchLoadMore.currentPage = newCurrentPage;
            this.searchLoadMore.totalPages = newTotalPages;
            this.searchLoadMore.hasMorePages = newHasMore;
            this.searchLoadMore.isLoading = false;
            
            // Update the loaded products count for the new search
            const newTotalProducts = parseInt(document.querySelector('.search-main__total-products')?.textContent) || 0;
            const newPaginationLimit = parseInt(document.querySelector('.search-main__current-limit')?.textContent) || 48;
            this.searchLoadMore.totalProducts = newTotalProducts;
            this.searchLoadMore.loadedProductsCount = Math.min(newPaginationLimit, newTotalProducts);
            this.searchLoadMore.initialLoadedProducts = Math.min(newPaginationLimit, newTotalProducts);
            
        }

        // Dispatch events (no URL update)
        const totalProdCount = this.getTotalProductCount();
        this.dispatchEvent(new CustomEvent('search:updated', { 
            bubbles: true,
            detail: { count: totalProdCount }
        }));
    }

    getTotalProductCount() {
        const totalProductsElement = document.querySelector('.search-main__total-products');
        return totalProductsElement ? parseInt(totalProductsElement.textContent) || 0 : 0;
    }

    setLoadingState(loading) {
        // No loading state needed since we have infinite scroll
    }

    updateUrl(url) {
        history.pushState({}, null, url);
    }

    handleFetchError(error) {
        // Hide skeleton cards on error
        this.hideSkeletonCards();
    }

    sanitizeUrl(url) {
        try {
            const urlObj = new URL(url);
            if (this.isValidUrl(urlObj.toString())) {
                return urlObj.toString();
            }
            return null;
        } catch {
            return null;
        }
    }

    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    applyDiscountBackgroundColors() {
        if (this.searchGrid && window.applyDiscountBackgroundColors) {
            window.applyDiscountBackgroundColors(this.searchGrid);
        }
    }

    makeProductCardsVisible() {
        if (this.searchGrid) {
            setTimeout(() => {
                const productCards = this.searchGrid.querySelectorAll('product-card');
                productCards.forEach(card => {
                    card.classList.add('product-card--visible');
                });
            }, 50);
        }
    }

    showSkeletonCards() {
        const skeletonContainer = document.querySelector('.search-skeleton-container');
        if (skeletonContainer) {
            skeletonContainer.style.display = 'grid';
        }
    }

    hideSkeletonCards() {
        const skeletonContainer = document.querySelector('.search-skeleton-container');
        if (skeletonContainer) {
            skeletonContainer.style.display = 'none';
        }
    }

    updatePaginationElements(doc) {
        // Update current limit
        const newCurrentLimit = doc.querySelector('.search-main__current-limit');
        const currentLimitElement = document.querySelector('.search-main__current-limit');
        if (newCurrentLimit && currentLimitElement) {
            currentLimitElement.textContent = newCurrentLimit.textContent;
        }

        // Update current page
        const newCurrentPage = doc.querySelector('.search-main__current-page');
        const currentPageElement = document.querySelector('.search-main__current-page');
        if (newCurrentPage && currentPageElement) {
            currentPageElement.textContent = newCurrentPage.textContent;
        }

        // Update total pages
        const newTotalPages = doc.querySelector('.search-main__total-pages');
        const totalPagesElement = document.querySelector('.search-main__total-pages');
        if (newTotalPages && totalPagesElement) {
            totalPagesElement.textContent = newTotalPages.textContent;
        }

        // Update has more pages
        const newHasMorePages = doc.querySelector('.search-main__has-more-pages');
        const hasMorePagesElement = document.querySelector('.search-main__has-more-pages');
        if (newHasMorePages && hasMorePagesElement) {
            hasMorePagesElement.setAttribute('data-has-more', newHasMorePages.getAttribute('data-has-more'));
        }

        // Update total products count
        const newTotalProducts = doc.querySelector('.search-main__total-products');
        const totalProductsElement = document.querySelector('.search-main__total-products');
        if (newTotalProducts && totalProductsElement) {
            totalProductsElement.textContent = newTotalProducts.textContent;
        }

        // Update header count
        const newHeaderCount = doc.querySelector('.search-main__header-count');
        const headerCountElement = document.querySelector('.search-main__header-count');
        if (newHeaderCount && headerCountElement) {
            headerCountElement.innerHTML = newHeaderCount.innerHTML;
        }

    }
}

// Register all web components
if (!customElements.get('search-main')) {
    customElements.define('search-main', SearchMain);
}

if (!customElements.get('search-grid')) {
    customElements.define('search-grid', SearchGrid);
}

if (!customElements.get('search-load-more')) {
    customElements.define('search-load-more', SearchLoadMore);
}
